// @ts-nocheck
import React, { useState } from 'react';

interface FeedItem {
  _id: string;
  title: string;
  feedUrl: string;
  unreadCount?: number;
  status: string;
}

interface CollectionItem {
  _id: string;
  name: string;
  color?: string;
  itemCount?: number;
}

interface TagItem {
  _id: string;
  name: string;
  normalizedName: string;
  itemCount?: number;
}

interface ReaderSidebarProps {
  currentView: string;
  selectedFeedId: string | null;
  selectedCollectionId: string | null;
  selectedTag: string | null;
  unreadCount: number;
  totalSaved: number;
  feeds: FeedItem[];
  collections: CollectionItem[];
  tags: TagItem[];
  onSelectView: (view: string) => void;
  onSelectFeed: (feedId: string | null) => void;
  onSelectCollection: (colId: string | null) => void;
  onSelectTag: (tag: string | null) => void;
  onOpenAddLink: () => void;
  onOpenAddFeed: () => void;
  onOpenManageFeeds: () => void;
  onOpenSettings: () => void;
  onOpenCreateCollection: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  inDrawer?: boolean;
}

export default function ReaderSidebar({
  currentView,
  selectedFeedId,
  selectedCollectionId,
  selectedTag,
  unreadCount,
  totalSaved,
  feeds = [],
  collections = [],
  tags = [],
  onSelectView,
  onSelectFeed,
  onSelectCollection,
  onSelectTag,
  onOpenAddLink,
  onOpenAddFeed,
  onOpenManageFeeds,
  onOpenSettings,
  onOpenCreateCollection,
  isCollapsed,
  onToggleCollapse,
  inDrawer = false,
}: ReaderSidebarProps) {
  const [feedsExpanded, setFeedsExpanded] = useState(true);
  const [collectionsExpanded, setCollectionsExpanded] = useState(true);
  const [tagsExpanded, setTagsExpanded] = useState(true);

  return (
    <aside className={`reader-sidebar ${isCollapsed ? 'collapsed' : ''} ${inDrawer ? 'in-drawer' : ''}`} aria-label="Reader Navigation">
      <div className="reader-sidebar-header">
        <button
          className="reader-btn reader-btn-primary"
          style={{ flex: 1 }}
          onClick={onOpenAddLink}
          title="Save link to library"
        >
          <i className="fi fi-rr-plus" style={{ fontSize: '0.8rem' }} />
          <span>Save Link</span>
        </button>
        <button
          className="reader-btn"
          onClick={onOpenAddFeed}
          title="Subscribe to RSS / Atom Feed"
        >
          <i className="fi fi-rr-rss" style={{ fontSize: '0.85rem' }} />
          <span>Feed</span>
        </button>
      </div>

      <nav className="reader-sidebar-nav-section">
        {/* Inbox */}
        <div
          className={`reader-nav-item ${currentView === 'inbox' && !selectedFeedId && !selectedCollectionId && !selectedTag ? 'active' : ''}`}
          onClick={() => {
            onSelectFeed(null);
            onSelectCollection(null);
            onSelectTag(null);
            onSelectView('inbox');
          }}
          role="button"
          tabIndex={0}
        >
          <div className="reader-nav-item-left">
            <i className="fi fi-rr-inbox" />
            <span>Inbox</span>
          </div>
          {unreadCount > 0 && <span className="reader-nav-badge">{unreadCount}</span>}
        </div>

        {/* Library */}
        <div
          className={`reader-nav-item ${currentView === 'library' && !selectedFeedId && !selectedCollectionId && !selectedTag ? 'active' : ''}`}
          onClick={() => {
            onSelectFeed(null);
            onSelectCollection(null);
            onSelectTag(null);
            onSelectView('library');
          }}
          role="button"
          tabIndex={0}
        >
          <div className="reader-nav-item-left">
            <i className="fi fi-rr-bookmark" />
            <span>Library</span>
          </div>
          <span className="reader-nav-badge">{totalSaved}</span>
        </div>

        {/* Favorites */}
        <div
          className={`reader-nav-item ${currentView === 'favorites' ? 'active' : ''}`}
          onClick={() => {
            onSelectFeed(null);
            onSelectCollection(null);
            onSelectTag(null);
            onSelectView('favorites');
          }}
          role="button"
          tabIndex={0}
        >
          <div className="reader-nav-item-left">
            <i className="fi fi-rr-star" />
            <span>Favorites</span>
          </div>
        </div>

        {/* Archive */}
        <div
          className={`reader-nav-item ${currentView === 'archive' ? 'active' : ''}`}
          onClick={() => {
            onSelectFeed(null);
            onSelectCollection(null);
            onSelectTag(null);
            onSelectView('archive');
          }}
          role="button"
          tabIndex={0}
        >
          <div className="reader-nav-item-left">
            <i className="fi fi-rr-box-alt" />
            <span>Archive</span>
          </div>
        </div>
      </nav>

      {/* FEEDS SECTION */}
      <div className="reader-sidebar-nav-section">
        <div className="reader-sidebar-section-title">
          <span
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => setFeedsExpanded(!feedsExpanded)}
          >
            <i
              className={`fi fi-rr-angle-${feedsExpanded ? 'down' : 'right'}`}
              style={{ fontSize: '0.65rem' }}
            />
            <span>Feeds ({feeds.length})</span>
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={onOpenAddFeed}
              title="Add feed"
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '2px' }}
            >
              <i className="fi fi-rr-plus" style={{ fontSize: '0.75rem' }} />
            </button>
            <button
              onClick={onOpenManageFeeds}
              title="Manage feeds"
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '2px' }}
            >
              <i className="fi fi-rr-settings" style={{ fontSize: '0.75rem' }} />
            </button>
          </div>
        </div>

        {feedsExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {feeds.length === 0 ? (
              <div style={{ padding: '6px 16px', fontSize: '0.78rem', color: 'var(--reader-text-subtle)' }}>
                No RSS subscriptions yet
              </div>
            ) : (
              feeds.map((feed) => (
                <div
                  key={feed._id}
                  className={`reader-nav-item ${selectedFeedId === feed._id ? 'active' : ''}`}
                  onClick={() => {
                    onSelectCollection(null);
                    onSelectTag(null);
                    onSelectFeed(feed._id);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="reader-nav-item-left">
                    <i
                      className="fi fi-rr-rss"
                      style={{ fontSize: '0.75rem', color: feed.status === 'error' ? '#ef4444' : 'inherit' }}
                    />
                    <span title={feed.title}>{feed.title}</span>
                  </div>
                  {feed.unreadCount > 0 && <span className="reader-nav-badge">{feed.unreadCount}</span>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* COLLECTIONS SECTION */}
      <div className="reader-sidebar-nav-section">
        <div className="reader-sidebar-section-title">
          <span
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => setCollectionsExpanded(!collectionsExpanded)}
          >
            <i
              className={`fi fi-rr-angle-${collectionsExpanded ? 'down' : 'right'}`}
              style={{ fontSize: '0.65rem' }}
            />
            <span>Collections ({collections.length})</span>
          </span>
          <button
            onClick={onOpenCreateCollection}
            title="Create collection"
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '2px' }}
          >
            <i className="fi fi-rr-plus" style={{ fontSize: '0.75rem' }} />
          </button>
        </div>

        {collectionsExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {collections.length === 0 ? (
              <div style={{ padding: '6px 16px', fontSize: '0.78rem', color: 'var(--reader-text-subtle)' }}>
                No collections created
              </div>
            ) : (
              collections.map((col) => (
                <div
                  key={col._id}
                  className={`reader-nav-item ${selectedCollectionId === col._id ? 'active' : ''}`}
                  onClick={() => {
                    onSelectFeed(null);
                    onSelectTag(null);
                    onSelectCollection(col._id);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="reader-nav-item-left">
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '2px',
                        backgroundColor: col.color || 'var(--reader-accent)',
                        display: 'inline-block',
                      }}
                    />
                    <span title={col.name}>{col.name}</span>
                  </div>
                  {col.itemCount > 0 && <span className="reader-nav-badge">{col.itemCount}</span>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* TAGS SECTION */}
      <div className="reader-sidebar-nav-section">
        <div className="reader-sidebar-section-title">
          <span
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => setTagsExpanded(!tagsExpanded)}
          >
            <i
              className={`fi fi-rr-angle-${tagsExpanded ? 'down' : 'right'}`}
              style={{ fontSize: '0.65rem' }}
            />
            <span>Tags ({tags.length})</span>
          </span>
        </div>

        {tagsExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {tags.length === 0 ? (
              <div style={{ padding: '6px 16px', fontSize: '0.78rem', color: 'var(--reader-text-subtle)' }}>
                No tags added yet
              </div>
            ) : (
              tags.map((tag) => (
                <div
                  key={tag._id}
                  className={`reader-nav-item ${selectedTag === tag.normalizedName ? 'active' : ''}`}
                  onClick={() => {
                    onSelectFeed(null);
                    onSelectCollection(null);
                    onSelectTag(tag.normalizedName);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="reader-nav-item-left">
                    <i className="fi fi-rr-label" style={{ fontSize: '0.75rem' }} />
                    <span title={tag.name}>#{tag.name}</span>
                  </div>
                  {tag.itemCount > 0 && <span className="reader-nav-badge">{tag.itemCount}</span>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer / Settings link */}
      <div style={{ marginTop: 'auto', padding: '12px 16px', borderTop: '1px solid var(--reader-border)' }}>
        <div
          className="reader-nav-item"
          style={{ padding: '6px 0', minHeight: 'auto' }}
          onClick={onOpenSettings}
          role="button"
          tabIndex={0}
        >
          <div className="reader-nav-item-left">
            <i className="fi fi-rr-settings" />
            <span>Settings & Portability</span>
          </div>
        </div>
      </div>
    </aside>
  );
}
